<template>
  <div class="row">
    <div class="col-12">
      <div class="d-flex align-items-center justify-content-center day-switcher">
        <button class="btn btn-sm btn-outline-secondary date-nav-btn" @click="changeDay(-1)">
          <i class="material-icons">chevron_left</i>
        </button>
        <h6 class="mb-0 mx-3 date-display">{{ displayDate }}</h6>
        <button class="btn btn-sm btn-outline-secondary date-nav-btn" @click="changeDay(1)">
          <i class="material-icons">chevron_right</i>
        </button>
      </div>
      <card :title="$t('projects.projects')">
        <template #before-table>
          <div class="row filter-row">
            <div class="col-12 col-md-4 mb-2">
              <el-input
                v-model="searchQuery"
                class="search-input"
                clearable
                :prefix-icon="Search"
                :placeholder="$t('common.searchRecords')"
                aria-controls="datatables"
                @keyup="search"
                @clear="handleClearSearch"
              />
            </div>
            <div class="col-6 col-md-4 mb-2">
              <el-date-picker
                v-model="chosenDate"
                class="w-100"
                format="DD/MM/YYYY"
                value-format="YYYY-MM-DD"
                :placeholder="formattedDate"
              />
            </div>
            <div class="col-6 col-md-4 mb-2">
              <el-select
                v-model="statusFilter"
                :class="selectClass"
                :placeholder="$t('common.status')"
                class="w-100"
                @change="fetchData"
              >
                <el-option
                  v-for="status in statusOptions"
                  :key="status.id"
                  :label="status.status"
                  :value="status.status"
                />
              </el-select>
            </div>
          </div>
        </template>

        <div class="table-responsive">
          <el-table :data="assignments" class="w-100" @row-click="goToProject">
            <el-table-column
              v-for="column in tableColumns"
              :key="column.label"
              :min-width="column.minWidth"
              :prop="column.prop"
              :label="column.label"
            />
            <el-table-column :min-width="135" align="right" label="">
              <template #default="{ row }">
                <!-- Standard mode (no assignment types) -->
                <template v-if="!enableProjectAssignmentTypes">
                  <material-button
                    :class="getButtonClass(row)"
                    size="sm"
                    @click.stop="showActiveAssignmentInfo(row)"
                  >
                    <i v-if="row.status === $t('projectAssignments.notFinished')" class="material-icons">rocket_launch</i>
                    <i v-else-if="row.status === $t('projectAssignments.ongoing')" class="material-icons">play_circle</i>
                    <i v-else class="material-icons">info</i>
                  </material-button>
                </template>
                <!-- Assignment Types mode -->
                <template v-else>
                  <material-button
                    v-for="assignment in row.projectAssignments"
                    :key="assignment.id"
                    :class="getButtonClass(assignment)"
                    size="sm"
                    class="me-1"
                    @click.stop="showActiveAssignmentInfo({ ...assignment, name: row.name, projectId: row.projectId })"
                  >
                    <span>{{ assignment.projectAssignmentType?.type?.charAt(0).toUpperCase() || '?' }}</span>
                  </material-button>
                </template>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </card>
    </div>
  </div>

  <!-- Assignment Info Modal -->
  <modal v-if="activeAssignment !== null" v-model:show="showAssignmentModal">
    <div class="p-3">
      <div class="d-flex align-items-center mb-3">
        <div class="icon icon-shape shadow text-center border-radius-md me-3" :class="getAssignmentModalIconClass()">
          <i class="material-icons opacity-10 pt-1">event</i>
        </div>
        <div>
          <h5 class="mb-0">{{ activeAssignment.name }} - {{ activeAssignment.projectAssignmentType?.type || '' }}</h5>
          <span class="badge mt-1" :class="getAssignmentStatusBadgeClass()">{{ activeAssignment.status }}</span>
        </div>
      </div>
      <div class="ps-5 ms-3">
        <div class="mb-2">
          <i class="material-icons text-sm align-middle me-2 text-secondary">schedule</i>
          <span class="text-sm">{{ activeAssignmentTimeString }}</span>
        </div>
        <div v-if="activeAssignment.userId !== null" class="mb-2">
          <i class="material-icons text-sm align-middle me-2 text-secondary">person</i>
          <span class="text-sm">{{ activeAssignment.user }}</span>
        </div>
        <div v-if="activeAssignment.comment !== ''" class="mb-2">
          <i class="material-icons text-sm align-middle me-2 text-secondary">chat</i>
          <span class="text-sm">{{ activeAssignment.comment }}</span>
        </div>
      </div>
      <!-- Assign to me button -->
      <div v-if="activeAssignment.userId === null" class="mt-3">
        <material-button variant="gradient" color="success" full-width @click="assignAssignmentToUser">
          <i class="material-icons me-1">person_add</i>
          {{ $t('projectAssignments.assignToMe') }}
        </material-button>
      </div>
      <!-- End assignment button -->
      <div v-if="activeAssignment.userId === userId && activeAssignment.active" class="mt-2">
        <material-button variant="gradient" color="dark" full-width @click="beginEndProjectAssignment">
          <i class="material-icons me-1">check_circle</i>
          {{ $t('projectAssignments.endAssignment') }}
        </material-button>
      </div>
    </div>
  </modal>

  <!-- Close Assignment Modal -->
  <modal v-if="activeAssignment !== null" v-model:show="showCloseAssignmentModal">
    <div class="p-3">
      <div class="d-flex align-items-center mb-3">
        <div class="icon icon-shape bg-gradient-warning shadow text-center border-radius-md me-3">
          <i class="material-icons opacity-10 pt-1">check_circle</i>
        </div>
        <div>
          <h5 class="mb-0">{{ activeAssignment.name }} - {{ activeAssignment.projectAssignmentType?.type || '' }}</h5>
          <span class="text-sm text-secondary">{{ $t('projectAssignments.closeAssignment') || 'Close Assignment' }}</span>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label text-uppercase text-secondary text-xs font-weight-bolder">{{ $t('projectAssignments.started') }}</label>
          <time-picker
            v-model="closeAssignmentStartTimePicker"
            class="w-100"
            :placeholder="$t('projectAssignments.started')"
          />
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label text-uppercase text-secondary text-xs font-weight-bolder">{{ $t('projectAssignments.ended') }}</label>
          <time-picker
            v-model="closeAssignmentEndTimePicker"
            class="w-100"
            :placeholder="$t('projectAssignments.ended')"
          />
        </div>
      </div>
      <div class="mb-3">
        <material-input-field
          id="closeCommentInput"
          v-model="closeAssignmentComment"
          :label="$t('projectAssignments.comment')"
          :placeholder="$t('projectAssignments.comment')"
          name="closeComment"
          variant="static"
        />
      </div>
      <div class="d-flex justify-content-end">
        <material-button variant="gradient" color="dark" @click="endProjectAssignment">
          <i class="material-icons me-1">check</i>
          {{ $t('common.confirm') }}
        </material-button>
      </div>
    </div>
  </modal>
</template>

<script>
import { Search } from '@element-plus/icons-vue';
import { shallowRef } from 'vue';
import { jwtDecode } from "jwt-decode";
import Cookies from 'js-cookie';
import api from "../../services/api";
import { ElInput, ElSelect, ElOption, ElTable, ElTableColumn } from 'element-plus';
import { getCustomerConfig } from '../Settings/customerConfigHelper';
import Card from "../../components/Card.vue";
import TimePicker from "../../components/TimePicker.vue";
import Modal from "../../components/Modals/Modal.vue";
import MaterialButton from "../../components/MaterialButton.vue";
import MaterialInputField from "../../components/MaterialInputField.vue";

export default {
  components: {
    ElInput,
    ElSelect,
    ElOption,
    ElTable,
    ElTableColumn,
    TimePicker,
    Card,
    Modal,
    MaterialButton,
    MaterialInputField,
  },
  data() {
    return {
      Search: shallowRef(Search),
      customerId: '',
      userId: '',
      userRole: '',
      enableProjectAssignmentTypes: false,
      activeAssignment: null,
      activeAssignmentTimeString: '',
      showAssignmentModal: false,
      showCloseAssignmentModal: false,
      closeAssignmentStartTimePicker: '',
      closeAssignmentEndTimePicker: '',
      closeAssignmentComment: '',
      apiDate: '',
      searchQuery: '',
      assignments: [],
      isMobile: window.innerWidth <= 1024,
      statusFilter: '',
      statusOptions: [
        { id: 'all', status: this.$t('projectAssignments.all') },
        { id: 'finished', status: this.$t('projectAssignments.finished') },
        { id: 'notFinished', status: this.$t('projectAssignments.notFinished') },
        { id: 'ongoing', status: this.$t('projectAssignments.ongoing') },
      ],
    };
  },
  computed: {
    displayDate() {
      if (!this.apiDate) return '';
      const date = new Date(this.apiDate + 'T00:00:00');
      const weekday = this.$t(`dateNames.weekdays[${date.getDay()}]`);
      const day = date.getDate();
      const month = this.$t(`dateNames.months[${date.getMonth()}]`);
      const year = date.getFullYear();
      return `${weekday}, ${day}. ${month} ${year}`;
    },
    selectClass() {
      const darkMode = localStorage.getItem('darkMode');
      return darkMode === 'true' ? 'select-primary' : 'select-standard';
    },
    tableColumns() {
      if (this.isMobile) {
        return [{ prop: 'name', label: this.$t('common.name'), minWidth: 170 }];
      }
      return [
        { prop: 'key', label: this.$t('projects.key'), minWidth: 100 },
        { prop: 'name', label: this.$t('common.name'), minWidth: 200 },
        { prop: 'status', label: this.$t('common.status'), minWidth: 100 },
        { prop: 'user', label: this.$t('common.user'), minWidth: 200 },
      ];
    },
  },
  watch: {},
  created() {
    this.updateIsMobile();
    window.addEventListener('resize', this.updateIsMobile);

    const today = new Date();
    this.apiDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    if (this.isMobile) {
      this.statusFilter = this.$t('projectAssignments.all');
    }

    const savedSearchQuery = Cookies.get('todaysProjectsSearchQuery');
    if (savedSearchQuery) this.searchQuery = savedSearchQuery;

    const token = Cookies.get('accessToken');
    const decodedToken = jwtDecode(token);
    this.userRole = decodedToken.role;
    this.customerId = decodedToken.CustomerId;
    this.userId = decodedToken.UserIdentifier;

    getCustomerConfig('EnableProjectAssignmentTypes').then(config => {
      this.enableProjectAssignmentTypes = config !== '' ? config : false;
      this.fetchData();
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateIsMobile);
  },
  methods: {
    updateIsMobile() { this.isMobile = window.innerWidth <= 1024; },

    changeDay(offset) {
      const current = new Date(this.apiDate + 'T00:00:00');
      current.setDate(current.getDate() + offset);
      this.apiDate = this.formatDate(current);
      this.fetchData();
    },

    // Assignment info
    showActiveAssignmentInfo(row) {
      this.activeAssignment = row;
      this.activeAssignmentTimeString = '';
      if (this.activeAssignment.endTime === null) {
        this.activeAssignmentTimeString = this.formatTimeFromApiDate(this.activeAssignment.startTime);
      } else {
        this.activeAssignmentTimeString = this.formatTimeFromApiDate(this.activeAssignment.startTime) + ' - ' + this.formatTimeFromApiDate(this.activeAssignment.endTime);
      }
      this.showAssignmentModal = true;
    },

    async assignAssignmentToUser() {
      try {
        await api.put('/Project/Assignment/' + this.activeAssignment.id, {
          projectId: this.activeAssignment.projectId,
          userId: this.userId,
          multiplier: this.activeAssignment.multiplier,
          startTime: this.activeAssignment.startTime,
          comment: this.activeAssignment.comment,
          active: true,
        });
        this.fetchData();
        this.showAssignmentModal = false;
        this.$toast.success(this.$t('projectAssignments.assignmentAssigned'), { timeout: 1500 });
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.somethingWentWrong'), { timeout: 0 });
      }
    },

    beginEndProjectAssignment() {
      this.closeAssignmentComment = this.activeAssignment.comment;
      this.closeAssignmentStartTimePicker = this.formatTimeFromApiDate(this.activeAssignment.startTime);
      this.showAssignmentModal = false;
      this.showCloseAssignmentModal = true;
    },

    async endProjectAssignment() {
      const startTime = `${this.activeAssignment.startTime.split('T')[0]}T${this.closeAssignmentStartTimePicker}:00`;
      const endTime = `${this.activeAssignment.startTime.split('T')[0]}T${this.closeAssignmentEndTimePicker}:00`;

      if (startTime > endTime || this.closeAssignmentEndTimePicker === '') {
        this.$toast.info(this.$t('errors.startDateBiggerThanEndDate'), { timeout: 2000 });
        return;
      }

      try {
        await api.put('/Project/Assignment/' + this.activeAssignment.id, {
          projectId: this.activeAssignment.projectId,
          userId: this.activeAssignment.userId,
          multiplier: this.activeAssignment.multiplier,
          startTime: startTime,
          endTime: endTime,
          comment: this.closeAssignmentComment,
          active: false,
        });

        this.showCloseAssignmentModal = false;
        this.activeAssignment = null;
        this.closeAssignmentComment = '';
        this.closeAssignmentEndTimePicker = '';
        this.closeAssignmentStartTimePicker = '';
        this.fetchData();
        this.$toast.success(this.$t('projectAssignments.assignmentClosed'), { timeout: 1500 });
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.somethingWentWrong'), { timeout: 0 });
      }
    },

    // Status helpers
    getButtonClass(row) {
      switch (row.status) {
        case this.$t('projectAssignments.notFinished'): return 'action-button';
        case this.$t('projectAssignments.ongoing'): return 'active-button';
        default: return '';
      }
    },
    getAssignmentModalIconClass() {
      if (!this.activeAssignment) return 'bg-gradient-secondary';
      if (this.activeAssignment.active) return 'bg-gradient-success';
      if (this.activeAssignment.status === this.$t('projectAssignments.notFinished')) return 'bg-gradient-danger';
      return 'bg-gradient-secondary';
    },
    getAssignmentStatusBadgeClass() {
      if (!this.activeAssignment) return 'bg-gradient-secondary';
      if (this.activeAssignment.active) return 'bg-gradient-success';
      if (this.activeAssignment.status === this.$t('projectAssignments.notFinished')) return 'bg-gradient-danger';
      return 'bg-gradient-secondary';
    },

    // Navigation
    goToProject(item) {
      this.$router.push({ name: 'Project', params: { projectId: item.projectId } });
    },

    // Helpers
    formatDate(dateString) {
      const date = new Date(dateString);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    },
    formatTimeFromApiDate(dateTime) {
      if (!dateTime) return null;
      const date = new Date(dateTime);
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    },
    getItemStatus(item) {
      if (item.active) return this.$t('projectAssignments.ongoing');
      if (!item.active && item.endTime === null) return this.$t('projectAssignments.notFinished');
      return this.$t('projectAssignments.finished');
    },

    // Search
    search(event) {
      const queryLength = this.searchQuery.length;
      if (event.key === 'Backspace' || event.key === 'Enter') {
        this.fetchData();
      } else if (queryLength > 1 && queryLength % 2 === 0) {
        this.fetchData();
      }
    },
    handleClearSearch() { this.fetchData(); },

    // Data
    fetchData() {
      let projectUrl = '/Project/Assignments';
      if (this.enableProjectAssignmentTypes) {
        projectUrl = '/Project/Assignments/Grouped';
      }
      if (this.searchQuery === '') {
        Cookies.remove('todaysProjectsSearchQuery');
        projectUrl += `?fromDate=${this.apiDate}&toDate=${this.apiDate}&preCreated=true&projectAssignmentWithTools=false`;
      } else {
        Cookies.set('todaysProjectsSearchQuery', this.searchQuery, { expires: new Date(new Date().getTime() + 60000) });
        projectUrl += `?fromDate=${this.apiDate}&toDate=${this.apiDate}&preCreated=true&projectAssignmentWithTools=false&searchString=${this.searchQuery}`;
      }

      api.get(projectUrl)
        .then(response => {
          if (!this.enableProjectAssignmentTypes) {
            this.assignments = response.data.map(item => ({
              id: item.id,
              key: item.project.key,
              name: item.project.name,
              projectId: item.project.id,
              status: this.getItemStatus(item),
              user: item.user !== null ? item.user.firstName + ' ' + item.user.lastName : '',
              startTime: item.startTime,
              endTime: item.endTime,
              comment: item.comment !== null ? item.comment : '',
              userId: item.userId,
              active: item.active,
              multiplier: item.multiplier,
              projectAssignmentType: item.projectAssignmentType,
            }));
          } else {
            this.assignments = response.data.map(group => {
              const projectAssignments = group.projectAssignments.map(item => {
                const assignment = {
                  id: item.id,
                  projectId: group.projectId,
                  startTime: item.startTime,
                  endTime: item.endTime,
                  comment: item.comment || '',
                  userId: item.userId,
                  active: item.active,
                  multiplier: item.multiplier,
                  user: item.user ? `${item.user.firstName} ${item.user.lastName}` : '',
                  projectAssignmentType: item.projectAssignmentType,
                };
                assignment.status = this.getItemStatus(assignment);
                return assignment;
              });
              const uniqueStatuses = [...new Set(projectAssignments.map(a => a.status))];
              const uniqueUsers = [...new Set(projectAssignments.map(a => a.user).filter(Boolean))];
              return {
                key: group.project.key,
                name: group.project.name,
                projectId: group.projectId,
                status: uniqueStatuses.join(', '),
                user: uniqueUsers.join(', '),
                projectAssignments,
              };
            });
          }

          // Client-side status filter
          if (this.statusFilter !== this.$t('projectAssignments.all') && this.statusFilter !== '') {
            if (!this.enableProjectAssignmentTypes) {
              this.assignments = this.assignments.filter(x => x.status === this.statusFilter);
            } else {
              this.assignments = this.assignments.filter(group => group.projectAssignments.some(a => a.status === this.statusFilter));
            }
          }
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
        });
    },
  },
};
</script>

<style scoped>
.action-button {
  background-color: #fd5d93 !important;
}

.active-button {
  background-color: #1a77e0 !important;
}

.icon-shape {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
}

.icon-shape i {
  font-size: 1.25rem;
  color: #fff;
}

.form-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #7b809a;
  margin-bottom: 0.5rem;
  display: block;
}

.date-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 0.5rem;
}

.date-nav-btn .material-icons {
  font-size: 1.25rem;
}

.day-switcher {
  /* same distance from the navbar as the cards (Card.vue .card-margin) */
  margin-top: 20px;
}

/* Element Plus gives the date editor a fixed width; let it fill its filter column */
.filter-row :deep(.el-date-editor) {
  width: 100%;
}

.date-display {
  font-weight: 500;
  white-space: nowrap;
}

:deep(.el-table) .material-icons {
  font-size: 1.1rem;
  vertical-align: middle;
  line-height: 1;
}

:deep(.el-table) .btn .material-icons {
  font-size: 1.1rem;
}
</style>
